import React, { useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Stage, Layer, Image as KonvaImage, Text, Transformer, Rect, Group } from 'react-konva';
import useImage from 'use-image';
import { useEditor } from '../../context/EditorContext';
import { Trash2 } from 'lucide-react';
import { logger } from '../../utils/logger';
import { PRINT_REGIONS, EXPORT_PIXEL_RATIO } from '../../config/printAreas';

// ---- Constants (stable references, avoid re-renders) ----
const PLACEHOLDER_DASH = [5, 5];
const TRANSFORMER_BORDER_DASH = [10, 5];
const TRANSFORMER_ANCHORS = ['top-left', 'top-right', 'bottom-left', 'bottom-right', 'top-center', 'bottom-center'];
const ROTATION_SNAPS = [0, 45, 90, 135, 180, 225, 270, 315];
const MIN_ELEMENT_SIZE = 10;

const boundBoxFunc = (oldBox, newBox) => {
  if (newBox.width < MIN_ELEMENT_SIZE || newBox.height < MIN_ELEMENT_SIZE) {
    return oldBox;
  }
  return newBox;
};

// ---- Element sub-components ----
const ElementTransformer = ({ trRef }) => (
  <Transformer
    ref={trRef}
    flipEnabled={false}
    boundBoxFunc={boundBoxFunc}
    rotateEnabled={true}
    enabledAnchors={TRANSFORMER_ANCHORS}
    anchorSize={18}
    anchorCornerRadius={9}
    anchorStroke="#2563EB"
    anchorFill="#FFFFFF"
    anchorStrokeWidth={3}
    borderStroke="#2563EB"
    borderStrokeWidth={3}
    borderDash={TRANSFORMER_BORDER_DASH}
    rotateAnchorOffset={40}
    rotationSnaps={ROTATION_SNAPS}
  />
);

const ImageElement = ({ element, image, isLoading, shapeRef, onSelect, onChange, onTransformEnd }) => (
  <Group
    x={element.x}
    y={element.y}
    rotation={element.rotation || 0}
    draggable={true}
    onClick={onSelect}
    onTap={onSelect}
    onDragEnd={(e) => onChange({ x: e.target.x(), y: e.target.y() })}
  >
    {image ? (
      <KonvaImage
        ref={shapeRef}
        x={0}
        y={0}
        width={element.width}
        height={element.height}
        image={image}
        onTransformEnd={onTransformEnd}
      />
    ) : (
      <Rect
        ref={shapeRef}
        x={0}
        y={0}
        width={element.width || 100}
        height={element.height || 100}
        fill={isLoading ? '#e0e7ff' : '#fee2e2'}
        stroke={isLoading ? '#6366f1' : '#ef4444'}
        strokeWidth={2}
        dash={PLACEHOLDER_DASH}
        onTransformEnd={onTransformEnd}
      />
    )}
  </Group>
);

const TextElement = ({ element, shapeRef, onSelect, onChange, onTransformEnd }) => (
  <Text
    ref={shapeRef}
    x={element.x}
    y={element.y}
    width={element.width}
    height={element.height}
    rotation={element.rotation || 0}
    draggable={true}
    onClick={onSelect}
    onTap={onSelect}
    onDragEnd={(e) => onChange({ x: e.target.x(), y: e.target.y() })}
    onTransformEnd={onTransformEnd}
    text={element.text}
    fontSize={element.fontSize || 20}
    fontFamily={element.fontFamily || 'Arial'}
    fill={element.fill || '#000000'}
    fontStyle={element.fontStyle || 'normal'}
  />
);

const DesignElement = ({ element, isSelected, onSelect, onChange }) => {
  const shapeRef = useRef();
  const trRef = useRef();
  const [image, status] = useImage(element.type === 'image' ? element.src : null, 'anonymous');

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected]);

  const handleTransformEnd = () => {
    const node = shapeRef.current;
    if (!node) return;

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();
    node.scaleX(1);
    node.scaleY(1);

    onChange({
      x: node.x(),
      y: node.y(),
      width: Math.max(MIN_ELEMENT_SIZE, node.width() * scaleX),
      height: Math.max(MIN_ELEMENT_SIZE, node.height() * scaleY),
      rotation: node.rotation(),
    });
  };

  return (
    <>
      {element.type === 'image' && (
        <ImageElement
          element={element}
          image={image}
          isLoading={status === 'loading'}
          shapeRef={shapeRef}
          onSelect={onSelect}
          onChange={onChange}
          onTransformEnd={handleTransformEnd}
        />
      )}
      {element.type === 'text' && (
        <TextElement
          element={element}
          shapeRef={shapeRef}
          onSelect={onSelect}
          onChange={onChange}
          onTransformEnd={handleTransformEnd}
        />
      )}
      {isSelected && <ElementTransformer trRef={trRef} />}
    </>
  );
};

export const DesignCanvas = forwardRef(({ width = 400, height = 533, view = 'front', scale = 1 }, ref) => {
  const {
    view: currentEditorView,
    getElementsForView,
    selectedElementId,
    setSelectedElementId,
    updateElement,
    clearSelection,
    removeElement,
  } = useEditor();

  const stageRef = useRef();
  const containerRef = useRef();

  // Determine if this canvas is the currently active view
  const isActiveView = view === currentEditorView;

  // Get elements for THIS canvas's view (not necessarily the current editor view)
  const elements = getElementsForView(view);

  // Handle keyboard delete (Backspace or Delete key)
  const handleKeyDown = useCallback((e) => {
    if (!isActiveView) return;
    if ((e.key === 'Backspace' || e.key === 'Delete') && selectedElementId) {
      // Don't delete if user is typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      e.preventDefault();
      removeElement(selectedElementId);
    }
  }, [selectedElementId, removeElement, isActiveView]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleDeleteSelected = useCallback(() => {
    if (selectedElementId && isActiveView) {
      removeElement(selectedElementId);
    }
  }, [selectedElementId, removeElement, isActiveView]);

  // Get selected element position for delete button (only for active view)
  const selectedElement = isActiveView ? elements.find(el => el.id === selectedElementId) : null;

  useImperativeHandle(ref, () => ({
    // Export ONLY the printable region as a high-resolution PNG.
    // The crop guarantees WYSIWYG: the dashed guide area maps 1:1 to
    // Printful's print area, so on-shirt size matches the preview.
    exportCanvas: () => {
      if (!stageRef.current) {
        return null;
      }
      const region = PRINT_REGIONS[view];
      try {
        return stageRef.current.toDataURL({
          x: region.x * scale,
          y: region.y * scale,
          width: region.width * scale,
          height: region.height * scale,
          pixelRatio: EXPORT_PIXEL_RATIO / scale,
          mimeType: 'image/png',
          quality: 1,
        });
      } catch (error) {
        logger.error(`Canvas export failed for ${view} view:`, error);
        return null;
      }
    },
    // Get reference to the Konva stage
    getStageRef: () => stageRef.current,
    // Check if canvas has any elements
    hasDesigns: () => elements.length > 0,
  }));

  const checkDeselect = (e) => {
    if (!isActiveView) return;
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) {
      clearSelection();
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: width * scale,
        height: height * scale,
        position: 'relative',
      }}
      tabIndex={0} // Make div focusable for keyboard events
    >
      <Stage
        ref={stageRef}
        width={width * scale}
        height={height * scale}
        scaleX={scale}
        scaleY={scale}
        onMouseDown={checkDeselect}
        onTouchStart={checkDeselect}
        listening={isActiveView} // Only listen to events if this is the active view
      >
        <Layer>
          {elements.map((element) => (
            <DesignElement
              key={element.id}
              element={element}
              isSelected={isActiveView && element.id === selectedElementId}
              onSelect={() => {
                if (isActiveView) {
                  setSelectedElementId(element.id);
                }
              }}
              onChange={(newAttrs) => {
                if (isActiveView) {
                  updateElement(element.id, newAttrs);
                }
              }}
            />
          ))}
        </Layer>
      </Stage>

      {/* Print area guide - HTML overlay so it never appears in exports */}
      <div
        data-testid="print-area-guide"
        style={{
          position: 'absolute',
          left: PRINT_REGIONS[view].x * scale,
          top: PRINT_REGIONS[view].y * scale,
          width: PRINT_REGIONS[view].width * scale,
          height: PRINT_REGIONS[view].height * scale,
          border: '1.5px dashed rgba(99, 102, 241, 0.55)',
          borderRadius: 4,
          pointerEvents: 'none',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: -16,
            left: 0,
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'rgba(99, 102, 241, 0.75)',
            whiteSpace: 'nowrap',
          }}
        >
          Print area
        </span>
      </div>

      {/* Delete Button - appears near selected element */}
      {selectedElement && (
        <button
          onClick={handleDeleteSelected}
          className="absolute z-50 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 active:scale-95"
          style={{
            left: Math.min(Math.max((selectedElement.x + (selectedElement.width || 50)) * scale + 8, 8), width * scale - 40),
            top: Math.max(selectedElement.y * scale - 4, 4),
          }}
          title="Delete (Backspace)"
          data-testid="delete-design-btn"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
});

DesignCanvas.displayName = 'DesignCanvas';

export default DesignCanvas;
